import { useEffect, useMemo, useState } from 'react'

import { addMonths, addWeeks, endOfMonth, endOfWeek, startOfMonth, startOfWeek } from 'date-fns'

import { useAppDispatch, useAppSelector } from '@App/hooks'
import { selectCalendarEvents } from '@App/redux/slices/calendar/selectors'
import { Tabs } from '@App/ui/crypto_exchange/desktop/components/common/Tabs'
import { Button } from '@App/ui/crypto_exchange/desktop/components/controls/Button'

import { EventDetails } from '../EventDetails'
import { Events } from '../Events'
import { Filters } from '../Filters'
import { IMPACT_OPTIONS, ImpactFilter } from '../Filters/components/ImpactFilter'
import { RegionFilter } from '../Filters/components/RegionFilter'
import { CustomDateTab } from '../PeriodFilters/components/CustomDateTab'

import s from './styles.module.scss'
import { getEvents } from '@App/redux/slices/calendar/slice'

type DateFilter = {
  name: string
  from: Date
  to: Date | null
  value: string
}
type Filters = {
  regions: string[]
  impacts: string[]
  date: DateFilter
}

const makeFilter = (name: string, fromDate: Date, toDate: Date | null): DateFilter => {
  return {
    name,
    from: fromDate,
    to: toDate,
    value: name.toLowerCase(),
  }
}

export const CreateDateFilters = (now = new Date()) => {
  const thisWeekFrom = startOfWeek(now, { weekStartsOn: 1 })
  const thisWeekTo = endOfWeek(now, { weekStartsOn: 1 })

  const thisMonthFrom = startOfMonth(now)
  const thisMonthTo = endOfMonth(now)

  const nextWeek = addWeeks(now, 1)
  const nextWeekFrom = startOfWeek(nextWeek, { weekStartsOn: 1 })
  const nextWeekTo = endOfWeek(nextWeek, { weekStartsOn: 1 })

  const nextMonth = addMonths(now, 1)
  const nextMonthFrom = startOfMonth(nextMonth)
  const nextMonthTo = endOfMonth(nextMonth)

  return [
    makeFilter('This week', thisWeekFrom, thisWeekTo),
    makeFilter('This month', thisMonthFrom, thisMonthTo),
    makeFilter('Next week', nextWeekFrom, nextWeekTo),
    makeFilter('Next month', nextMonthFrom, nextMonthTo),
    makeFilter('Custom', thisWeekFrom, null),
  ]
}
export const Calendar = () => {
  const dispatch = useAppDispatch()
  const events = useAppSelector(selectCalendarEvents)

  const dateFilters = useMemo(() => CreateDateFilters(), [])

  const [selectedEventId, setSelectedEventId] = useState<number | null>(null)
  const [filters, setFilters] = useState<Filters>({
    regions: [],
    impacts: ['all', ...IMPACT_OPTIONS.map((o) => o.value)],
    date: dateFilters[0],
  })
  const [appliedFilters, setAppliedFilters] = useState<Filters>(filters)

  const regions = useAppSelector((state) => state.countries.data)

  const initFilters = () => {
    selectAllImpacts()
    if (regions.length) {
      setAppliedFilters((prev) => ({
        ...prev,
        impacts: ['all', ...IMPACT_OPTIONS.map((o) => o.value)],
        regions: ['all', ...regions.map((c) => c.key)],
      }))
      selectAllRegions()
    }
  }

  useEffect(() => {
    if (filters.date.to) {
      dispatch(getEvents({ from: filters.date.from, to: filters.date.to }))
    }
  }, [filters.date.from, filters.date.to])

  useEffect(initFilters, [regions.length])

  const selectAllRegions = () => {
    setFilters((prev) => ({
      ...prev,
      regions: ['all', ...regions.map((c) => c.key)],
    }))
  }
  const selectAllImpacts = () => {
    setFilters((prev) => ({
      ...prev,
      impacts: ['all', ...IMPACT_OPTIONS.map((o) => o.value)],
    }))
  }
  const selectAllOptions = {
    regions: selectAllRegions,
    impacts: selectAllImpacts,
  }

  const setFilter = (filter: keyof Omit<Filters, 'date'>) => (val: string) => {
    const filterExists = filters[filter].some((v) => v === val)
    if (!filterExists) {
      if (val !== 'all') {
        setFilters((prev) => ({
          ...prev,
          [filter]: [...prev[filter], val],
        }))
        return
      }
      selectAllOptions[filter]()
      return
    }
    if (val !== 'all') {
      setFilters((prev) => ({
        ...prev,
        [filter]: [...prev[filter].filter((r) => r !== val && r !== 'all')],
      }))
      return
    }
    setFilters((prev) => ({
      ...prev,
      [filter]: [],
    }))
  }
  const setDateFilter = (val: string) => {
    const filter = dateFilters.find((d) => d.value === val)
    if (!filter) return
    setFilters((prev) => ({
      ...prev,
      date: {
        ...filter,
      },
    }))
  }
  const setCustomDateValue = (val: { from: Date; to: Date | null }) => {
    setFilters((prev) => ({
      ...prev,
      date: {
        ...prev.date,
        from: val.from,
        to: val.to,
      },
    }))
  }
  const resetCustomDateValue = () => {
    setFilters((prev) => ({
      ...prev,
      date: {
        ...prev.date,
        from: dateFilters.at(-1)?.from!,
        to: dateFilters.at(-1)?.to!,
      },
    }))
  }
  const applyFilters = () => {
    setAppliedFilters(filters)
  }

  const filteredEvents = events.filter(
    (e) => appliedFilters.impacts.includes(e.impact) && appliedFilters.regions.includes(e.country.key),
  )

  return (
    <div className={s.container}>
      <Filters
        actions={
          <>
            <Button onClick={applyFilters}>Apply</Button>{' '}
            <Button valiant="text" onClick={initFilters}>
              Reset filters
            </Button>
          </>
        }
      >
        <ImpactFilter value={filters.impacts} setFilter={setFilter('impacts')} />
        <RegionFilter value={filters.regions} setFilter={setFilter('regions')} />
      </Filters>

      <Events
        events={filteredEvents}
        onRowClick={({ id }) => {
          setSelectedEventId(id)
        }}
        header={
          <Tabs
            tabs={dateFilters}
            activeTab={filters.date.value}
            setActiveTab={setDateFilter}
            renderTab={{
              custom: (isActive, setPrevTab) => {
                return (
                  <CustomDateTab
                    setPrevTab={setPrevTab}
                    isActive={isActive}
                    value={filters.date}
                    setValue={setCustomDateValue}
                    resetDate={resetCustomDateValue}
                  />
                )
              },
            }}
          />
        }
      />
      {selectedEventId && <EventDetails eventId={selectedEventId} onClose={() => setSelectedEventId(null)} />}
    </div>
  )
}
